// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/ThemeEmbed.module.css';
import {
	EmbedCard,
	EmbedSkeletonButton,
	EmbedSkeletonCircle,
	EmbedSkeletonSubtitle,
	EmbedSkeletonTitle,
} from '@app/features/messaging/components/embeds/embed_card/EmbedCard';
import cardStyles from '@app/features/messaging/components/embeds/embed_card/EmbedCard.module.css';
import {useEmbedSkeletonOverride} from '@app/features/messaging/components/embeds/embed_card/useEmbedSkeletonOverride';
import * as ThemeCommands from '@app/features/theme/commands/ThemeCommands';
import {useThemeExists} from '@app/features/theme/hooks/useThemeExists';
import {Button} from '@app/features/ui/button/Button';
import {Trans, useLingui} from '@lingui/react/macro';
import {PaletteIcon, QuestionIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

interface ThemeEmbedProps {
	themeId: string;
}
export const ThemeEmbed = observer(function ThemeEmbed({themeId}: ThemeEmbedProps) {
	const {t, i18n} = useLingui();
	const status = useThemeExists(themeId);
	const shouldForceSkeleton = useEmbedSkeletonOverride();
	const handleImport = () => {
		ThemeCommands.openAcceptModal(themeId, i18n);
	};
	if (shouldForceSkeleton || status === 'loading') {
		return <ThemeLoadingState />;
	}
	if (status === 'error') {
		return <ThemeUnavailableError message={t`This theme is no longer available.`} />;
	}
	return (
		<EmbedCard
			splashURL={null}
			icon={
				<div className={`${styles.iconCircle} ${styles.iconCircleActive}`}>
					<PaletteIcon size={24} weight="bold" className={styles.iconOnBrand} />
				</div>
			}
			title={<h3 className={`${cardStyles.title} ${cardStyles.titlePrimary}`}>{t`Shared Theme`}</h3>}
			subtitle={
				<span className={cardStyles.helpText}>
					<Trans>You've got CSS!</Trans>
				</span>
			}
			footer={
				<Button variant="primary" matchSkeletonHeight onClick={handleImport}>
					{t`Import Theme`}
				</Button>
			}
		/>
	);
});
const ThemeLoadingState = observer(() => {
	return (
		<EmbedCard
			splashURL={null}
			icon={<EmbedSkeletonCircle />}
			title={<EmbedSkeletonTitle />}
			subtitle={<EmbedSkeletonSubtitle />}
			footer={<EmbedSkeletonButton />}
		/>
	);
});
const ThemeUnavailableError = observer(({message}: {message: string | null}) => {
	const {t} = useLingui();
	return (
		<EmbedCard
			splashURL={null}
			icon={
				<div className={cardStyles.iconCircleDisabled}>
					<QuestionIcon className={cardStyles.iconError} />
				</div>
			}
			title={<h3 className={`${cardStyles.title} ${cardStyles.titleDanger}`}>{t`Theme unavailable`}</h3>}
			subtitle={<span className={cardStyles.helpText}>{message ?? t`This theme is no longer available.`}</span>}
			footer={
				<Button variant="primary" matchSkeletonHeight disabled>
					{t`Import unavailable`}
				</Button>
			}
		/>
	);
});
